import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { AddressCepNotice } from '@components/AddressCepNotice';
import { useAuth } from '@contexts/AuthContext';
import { useAddressCepCheck } from '@/hooks/useAddressCepCheck';
import { applyCepToDivergent } from '@/utils/addressCepCheck';
import { goodsDeliveryOrderService } from '../../services/goodsDeliveryOrderService';
import { BRAZILIAN_STATES } from '../../types/provider';
import {
  buildDeliveryOrderPayload,
  DeliveryItemRow,
  DeliveryOrderFormState,
  emptyItemRow,
  isDeliveryOrderOpen,
  itemErrorKey,
  itemRowsFromOrder,
  toDateInputValue,
  validateDeliveryOrderForm,
} from './goodsDeliveryOrderUtils';

const initialForm = (): DeliveryOrderFormState => ({
  description: '',
  recipientName: '',
  recipientPhone: '',
  volumeQuantity: '',
  expectedDeliveryDate: '',
  isEmergency: false,
  observation: '',
  zip: '',
  street: '',
  num: '',
  compl: '',
  district: '',
  city: '',
  state: '',
  items: [emptyItemRow()],
});

type TextField = Exclude<keyof DeliveryOrderFormState, 'isEmergency' | 'items'>;

export const GoodsDeliveryOrderFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user } = useAuth();

  const [form, setForm] = useState<DeliveryOrderFormState>(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Edição bloqueada: ordem entregue ou cancelada (o backend responderia 409). */
  const [lockedReason, setLockedReason] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing || !id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const order = await goodsDeliveryOrderService.getOrderById(parseInt(id, 10));
        if (cancelled) return;
        setForm({
          description: order.description ?? '',
          recipientName: order.recipient_name ?? '',
          recipientPhone: order.recipient_phone ?? '',
          volumeQuantity: order.volume_quantity != null ? String(order.volume_quantity) : '',
          expectedDeliveryDate: toDateInputValue(order.expected_delivery_date),
          isEmergency: !!order.is_emergency,
          observation: order.observation ?? '',
          zip: (order.ship_addr_zip ?? '').replace(/\D/g, ''),
          street: order.ship_addr_street ?? '',
          num: order.ship_addr_num ?? '',
          compl: order.ship_addr_compl ?? '',
          district: order.ship_addr_district ?? '',
          city: order.ship_addr_city ?? '',
          state: order.ship_addr_state ?? '',
          items: itemRowsFromOrder(order.items),
        });
        if (!isDeliveryOrderOpen(order)) {
          setLockedReason(order.is_canceled ? 'Esta ordem foi cancelada e não pode ser alterada.' : 'Esta ordem já foi entregue e não pode ser alterada.');
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar ordem de entrega');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEditing]);

  const setField = (field: TextField, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // ── CEP: preenchimento + conferência (mesmo fluxo do pedido de venda) ──
  // ShipAddrZip é nvarchar(8): o CEP viaja sem máscara.
  const addrCheck = useAddressCepCheck(
    { zip: form.zip, uf: form.state, city: form.city, district: form.district, street: form.street },
    {
      enabled: !loading,
      onFound: (cep) => {
        setForm((prev) => ({
          ...prev,
          street: prev.street.trim() ? prev.street : cep.street,
          district: prev.district.trim() ? prev.district : cep.district,
          city: prev.city.trim() ? prev.city : cep.city,
          state: prev.state.trim() ? prev.state : cep.uf,
        }));
      },
    },
  );

  const applyCepAddress = () => {
    if (!addrCheck.cepAddress) return;
    const next = applyCepToDivergent(
      { uf: form.state, city: form.city, district: form.district, street: form.street },
      addrCheck.cepAddress,
      addrCheck.divergences,
    );
    setForm((prev) => ({ ...prev, state: next.uf, city: next.city, district: next.district, street: next.street }));
  };

  // ── Itens ──
  const updateItem = (idx: number, field: keyof DeliveryItemRow, value: string) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    }));
    const key = itemErrorKey(idx, field);
    setFieldErrors((prev) => (prev[key] || prev.items ? { ...prev, [key]: '', items: '' } : prev));
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItemRow()] }));

  const removeItem = (idx: number) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    // Os índices mudam: limpa os erros de item (reaparecem no próximo salvar).
    setFieldErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('items.'))));
  };

  // ── Salvar ──
  const handleSave = async () => {
    if (lockedReason) return;
    const errors = validateDeliveryOrderForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstKey = Object.keys(errors)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }
    setFieldErrors({});

    try {
      setSaving(true);
      setError(null);
      if (isEditing) {
        await goodsDeliveryOrderService.updateOrder(
          parseInt(id!, 10),
          buildDeliveryOrderPayload(form, { clearBlankOptionals: true }),
        );
        navigate(`/goods-delivery-orders/${id}`);
      } else {
        const created = await goodsDeliveryOrderService.createOrder(buildDeliveryOrderPayload(form));
        navigate(created?.goods_delivery_orders_id ? `/goods-delivery-orders/${created.goods_delivery_orders_id}` : '/goods-delivery-orders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar ordem de entrega');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (key: string, extra = '') =>
    `input-field ${extra} ${fieldErrors[key] ? 'border-red-400 focus:ring-red-400' : ''}`;

  const fieldError = (name: string) =>
    fieldErrors[name] ? <p className="text-xs text-red-600 mt-1">{fieldErrors[name]}</p> : null;

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-500">Carregando ordem de entrega...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          icon={<ArrowLeft size={20} />}
          onClick={() => navigate(isEditing ? `/goods-delivery-orders/${id}` : '/goods-delivery-orders')}
        >
          Voltar
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? `Editar Ordem de Entrega #${id}` : 'Nova Ordem de Entrega'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados da ordem de entrega' : 'Preencha os dados para solicitar uma entrega avulsa'}
          </p>
        </div>
      </div>

      {lockedReason && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">{lockedReason}</div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      {/* Solicitante (readonly) */}
      <Card>
        <CardHeader title="Solicitante" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail do solicitante</label>
              <input type="text" className="input-field bg-slate-50" value={user?.email || ''} disabled readOnly />
              <p className="text-xs text-slate-500 mt-1">Preenchido automaticamente pelo sistema</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dados da entrega */}
      <Card>
        <CardHeader title="Dados da Entrega" />
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descrição da entrega <span className="text-red-500">*</span>
              </label>
              <textarea
                data-field="description"
                className={inputCls('description', 'min-h-[80px]')}
                placeholder="Descreva o que será entregue e a finalidade"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                maxLength={500}
              />
              {fieldErrors.description ? fieldError('description') : (
                <p className="text-xs text-slate-500 mt-1">{form.description.length}/500 caracteres</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Data prevista <span className="text-red-500">*</span>
                </label>
                <DateInputBR
                  data-field="expectedDeliveryDate"
                  className={inputCls('expectedDeliveryDate')}
                  value={form.expectedDeliveryDate}
                  onChange={(v) => setField('expectedDeliveryDate', v)}
                />
                {fieldError('expectedDeliveryDate')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantidade de volumes</label>
                <input
                  data-field="volumeQuantity"
                  type="text"
                  inputMode="numeric"
                  className={inputCls('volumeQuantity')}
                  placeholder="Número de volumes"
                  value={form.volumeQuantity}
                  onChange={(e) => setField('volumeQuantity', e.target.value.replace(/\D/g, ''))}
                />
                {fieldError('volumeQuantity')}
              </div>
              <div className="flex items-end">
                <label
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 w-full cursor-pointer ${
                    form.isEmergency ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 text-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                    checked={form.isEmergency}
                    onChange={(e) => setForm((prev) => ({ ...prev, isEmergency: e.target.checked }))}
                  />
                  <AlertTriangle size={16} className={form.isEmergency ? 'text-red-600' : 'text-slate-400'} />
                  <span className="text-sm font-medium">Emergência</span>
                </label>
              </div>
            </div>
            {form.isEmergency && (
              <p className="text-xs text-red-600 -mt-2">
                Entrega emergencial: aparece em destaque no sino e entra na rota já como prioridade.
              </p>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Observação</label>
              <textarea
                data-field="observation"
                className={inputCls('observation', 'min-h-[60px]')}
                placeholder="Instruções para o motorista (portaria, horário, referência...)"
                value={form.observation}
                onChange={(e) => setField('observation', e.target.value)}
                maxLength={1000}
              />
              {fieldErrors.observation ? fieldError('observation') : (
                <p className="text-xs text-slate-500 mt-1">{form.observation.length}/1000 caracteres</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Destinatário e endereço */}
      <Card>
        <CardHeader title="Destinatário e Endereço de Entrega" />
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Destinatário <span className="text-red-500">*</span>
                </label>
                <input
                  data-field="recipientName"
                  type="text"
                  className={inputCls('recipientName')}
                  placeholder="Nome de quem vai receber"
                  value={form.recipientName}
                  onChange={(e) => setField('recipientName', e.target.value)}
                  maxLength={100}
                />
                {fieldError('recipientName')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefone</label>
                <input
                  data-field="recipientPhone"
                  type="tel"
                  className={inputCls('recipientPhone')}
                  placeholder="(11) 99999-9999"
                  value={form.recipientPhone}
                  onChange={(e) => setField('recipientPhone', e.target.value)}
                  maxLength={20}
                />
                {fieldError('recipientPhone')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CEP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    data-field="zip"
                    type="text"
                    inputMode="numeric"
                    className={inputCls('zip')}
                    placeholder="00000000"
                    value={form.zip}
                    onChange={(e) => setField('zip', e.target.value.replace(/\D/g, '').slice(0, 8))}
                    maxLength={8}
                  />
                  {addrCheck.status === 'loading' && (
                    <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                </div>
                {fieldError('zip')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rua <span className="text-red-500">*</span>
                </label>
                <input
                  data-field="street"
                  type="text"
                  className={inputCls('street')}
                  placeholder="Logradouro"
                  value={form.street}
                  onChange={(e) => setField('street', e.target.value)}
                  maxLength={100}
                />
                {fieldError('street')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Número <span className="text-red-500">*</span>
                </label>
                <input
                  data-field="num"
                  type="text"
                  className={inputCls('num')}
                  placeholder="Nº"
                  value={form.num}
                  onChange={(e) => setField('num', e.target.value)}
                  maxLength={10}
                />
                {fieldError('num')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Complemento</label>
                <input
                  data-field="compl"
                  type="text"
                  className={inputCls('compl')}
                  placeholder="Complemento"
                  value={form.compl}
                  onChange={(e) => setField('compl', e.target.value)}
                  maxLength={100}
                />
                {fieldError('compl')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bairro <span className="text-red-500">*</span>
                </label>
                <input
                  data-field="district"
                  type="text"
                  className={inputCls('district')}
                  placeholder="Bairro"
                  value={form.district}
                  onChange={(e) => setField('district', e.target.value)}
                  maxLength={100}
                />
                {fieldError('district')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cidade <span className="text-red-500">*</span>
                </label>
                <input
                  data-field="city"
                  type="text"
                  className={inputCls('city')}
                  placeholder="Cidade"
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  maxLength={100}
                />
                {fieldError('city')}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Estado <span className="text-red-500">*</span>
                </label>
                <select
                  data-field="state"
                  className={inputCls('state')}
                  value={form.state}
                  onChange={(e) => setField('state', e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label} ({s.value})
                    </option>
                  ))}
                </select>
                {fieldError('state')}
              </div>
            </div>

            <AddressCepNotice check={addrCheck} onApplyCep={applyCepAddress} />
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader title="Itens da Entrega" />
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Informe o que será entregue (texto livre). Quantidade aceita vírgula decimal, ex.: 1,5.
          </p>
          <div data-field="items" tabIndex={-1} className="space-y-2">
            <div className="hidden md:grid md:grid-cols-12 gap-2 text-xs font-medium text-slate-600 px-1">
              <span className="md:col-span-7">Descrição *</span>
              <span className="md:col-span-2">Quantidade *</span>
              <span className="md:col-span-2">Unidade</span>
              <span className="md:col-span-1" />
            </div>
            {form.items.map((row, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
                <div className="md:col-span-7">
                  <input
                    data-field={itemErrorKey(idx, 'description')}
                    type="text"
                    className={inputCls(itemErrorKey(idx, 'description'))}
                    placeholder="Descrição do item"
                    value={row.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    maxLength={200}
                  />
                  {fieldError(itemErrorKey(idx, 'description'))}
                </div>
                <div className="md:col-span-2">
                  <input
                    data-field={itemErrorKey(idx, 'quantity')}
                    type="text"
                    inputMode="decimal"
                    className={inputCls(itemErrorKey(idx, 'quantity'))}
                    placeholder="1"
                    value={row.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value.replace(/[^\d.,]/g, ''))}
                  />
                  {fieldError(itemErrorKey(idx, 'quantity'))}
                </div>
                <div className="md:col-span-2">
                  <input
                    data-field={itemErrorKey(idx, 'unit')}
                    type="text"
                    className={inputCls(itemErrorKey(idx, 'unit'))}
                    placeholder="un, cx, kg"
                    value={row.unit}
                    onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                    maxLength={10}
                  />
                  {fieldError(itemErrorKey(idx, 'unit'))}
                </div>
                <div className="md:col-span-1 flex md:justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Trash2 size={14} />}
                    onClick={() => removeItem(idx)}
                    disabled={form.items.length <= 1}
                    className="text-red-500 hover:text-red-700 mt-1"
                    title="Remover item"
                  />
                </div>
              </div>
            ))}
            {fieldErrors.items && <p className="text-xs text-red-600">{fieldErrors.items}</p>}
          </div>
          <Button variant="outline" icon={<Plus size={16} />} onClick={addItem} className="mt-4">
            Adicionar item
          </Button>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex flex-col gap-3">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(isEditing ? `/goods-delivery-orders/${id}` : '/goods-delivery-orders')}
          >
            Cancelar
          </Button>
          <Button
            icon={saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            onClick={handleSave}
            disabled={saving || !!lockedReason}
          >
            {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Ordem de Entrega'}
          </Button>
        </div>
      </div>
    </div>
  );
};
